"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleQuestionMark as CircleHelp, Phone, ArrowRight } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const faqItems = [{
  id: "item-1",
  question: "Do kitchen renovations in Brookhaven require permits through DeKalb County?",
  answer: "Yes. Most kitchen remodels involving layout changes, electrical updates, or plumbing alterations require residential permits through the DeKalb County Department of Planning & Sustainability. Our team handles the entire permit application, plan filings, and scheduled building code inspections from start to finish."
}, {
  id: "item-2",
  question: "Can we remain living in our home during the kitchen remodel?",
  answer: "Most homeowners choose to stay in their homes during renovation. We seal off active construction zones with heavy-duty dust containment walls, run HEPA air scrubbers, and can help set up a clean temporary kitchenette with basic refrigeration and prep space to protect your family's daily routine."
}, {
  id: "item-3",
  question: "How does Brookhaven Elevation Renovations assist with material selections?",
  answer: "We walk you through a curated design atelier experience. Our team guides you across trusted local stone yards, bespoke cabinet makers, tile showrooms, and fixture partners to evaluate natural quartzite, honed marble, solid brass hardware, and panel-ready appliances under real lighting conditions."
}, {
  id: "item-4",
  question: "What warranty and craftsmanship coverage is included?",
  answer: "Every kitchen remodel includes workmanship coverage on cabinetry installation, structural carpentry, and mechanical connections. In addition, premium fixtures and major appliances carry their manufacturer warranties, backed by our post-completion support."
}];
export default function Faqs() {
  const shouldReduceMotion = useReducedMotion();
  const revealVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 15
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="frequently-asked-questions" data-nav="light" className="bg-background text-foreground py-20 md:py-24 relative overflow-hidden">
      <div className="container mx-auto px-5 sm:px-8 max-w-4xl">
        <motion.div variants={revealVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="flex flex-col items-center text-center">
          <Badge variant="outline" className="mb-4 rounded-full border-primary/40 bg-primary/10 text-primary px-3.5 py-1 text-xs font-semibold tracking-[0.14em] uppercase">
            Kitchen planning &amp; guidance
          </Badge>

          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-foreground max-w-2xl">
            Frequently asked questions
          </h2>

          <p className="mt-4 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
            Straightforward answers about permits, daily living, material selections, and our Brookhaven build process.
          </p>

          <div className="h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent w-36 my-8" />
        </motion.div>

        <motion.div variants={revealVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="rounded-2xl border border-border/70 bg-card p-6 sm:p-8 md:p-10 shadow-sm">
          <Accordion type="single" collapsible defaultValue="item-1" className="w-full divide-y divide-border/60">
            {faqItems.map((faq, index) => <div key={faq.id} data-index={index}>
                <AccordionItem value={faq.id} className="border-none py-2 first:pt-0 last:pb-0">
                  <AccordionTrigger className="text-left font-sans text-base sm:text-lg font-semibold text-foreground hover:no-underline hover:text-primary transition-colors py-4 group">
                    <span className="flex items-center gap-3 pr-4">
                      <CircleHelp className="size-4 shrink-0 text-primary opacity-80 group-hover:opacity-100 transition-opacity" />
                      <span>{faq.question}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pl-7 pr-2 pb-4 pt-1">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              </div>)}
          </Accordion>
        </motion.div>

        <motion.div variants={revealVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="mt-12 text-center flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-muted-foreground">
          <span>Have a question about an upcoming renovation?</span>
          <div className="flex items-center gap-3">
            <Button asChild variant="outline" size="sm" className="border-border hover:border-primary/50">
              <a href="tel:+14708479076" className="inline-flex items-center gap-2">
                <Phone className="size-3.5 text-primary" />
                <span>(470) 847-9076</span>
              </a>
            </Button>
            <Button asChild size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="/ContactRequestAnEstimatePage" className="inline-flex items-center gap-1.5">
                <span>Request consultation</span>
                <ArrowRight className="size-3.5" />
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}